import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react'
import Loader from 'react-loader-spinner'
import styled from 'styled-components'
import * as Yup from 'yup'
import { useDispatch } from 'react-redux'
import { AxiosError } from 'axios'

import ButtonBase from 'components/Button'
import { Input, Label, Error } from 'components/Input'
import { useInput } from 'hooks/useInput'
import * as carrierService from 'services/carriers'
import * as carrierActions from 'features/carriers/actions'

const Wrapper = styled.div`
  margin-top: 20px;
`
const AddNewButton = styled(ButtonBase)`
  background: ${({ theme }) => theme.colors.light};
  border: unset;
  width: 145px;
  border-radius: 0;
`
const InviteButton = styled(ButtonBase)`
  width: 60px;
  justify-self: end;
`
const FormContainer = styled.div`
  background: ${({ theme }) => theme.colors.light};
  display: grid;
  padding: 20px 15px;
  grid-column-gap: 15px;
  grid-template-columns: 3fr 2fr 3fr 1fr;
`
const InvisibleText = styled.span`
  display: block;
  color: ${({ theme }) => theme.colors.light};
`

type Errors = Record<'name' | 'dot' | 'email', string>

const schema = Yup.object().shape({
  name: Yup.string()
    .max(100, 'Name is too long.')
    .required('This field is required.'),
  dot: Yup.string()
    .max(20, 'DOT Number is too long.')
    .required('This field is required.'),
  email: Yup.string()
    .email('Must be a valid email.')
    .max(50, 'Email is too long.')
    .required('This field is required.'),
})

function parseYupError(error: Yup.ValidationError): Errors {
  return error.inner.reduce(
    (acc: Record<string, string>, curr: Yup.ValidationError) => {
      acc[curr.path] = curr.message
      return acc
    },
    {}
  )
}

function parseAxiosError(error: AxiosError): Errors | null {
  if (error.response?.status !== 400) return null
  return Object.keys(error.response?.data)
    .filter((key) => ['dot', 'name', 'email'].includes(key))
    .reduce((acc: Record<string, string>, curr: string) => {
      acc[curr] = error.response?.data[curr].join('')
      return acc
    }, {})
}

async function createCarrier(
  carrier: Omit<Carrier, 'id'>,
  rfpId?: number
): Promise<Carrier> {
  await schema.validate(carrier, { abortEarly: false })
  const data = await carrierService.create(carrier, rfpId)
  return data
}

interface Props {
  rfpId?: number
  toggleCarrier(carrier: Carrier, isSelected: boolean): void
}

export default function AddNewCarrier({ toggleCarrier, rfpId }: Props) {
  const dispatch = useDispatch()
  const inputRef = useRef<HTMLInputElement>(null)
  const [isEditing, setEditing] = useState<boolean>(false)
  const [isCreating, setCreating] = useState<boolean>(false)
  const [errors, setErrors] = useState<Errors>({} as Errors)
  const { onClear: _, ...name } = useInput('')
  const { onClear: __, ...dot } = useInput('')
  const { onClear: ___, ...email } = useInput('')

  const resetForm = useCallback(() => {
    name.setValue('')
    dot.setValue('')
    email.setValue('')
  }, [email, name, dot])

  const toggleEditing = useCallback(() => {
    setEditing(!isEditing)
    resetForm()
  }, [isEditing, resetForm])

  const carrier = useMemo(
    () => ({
      name: name.value as string,
      dot: dot.value as string,
      email: email.value as string,
    }),
    [name, dot, email]
  )

  const createAndInvite = useCallback(async () => {
    try {
      setCreating(true)
      const createdCarrier = await createCarrier(carrier, rfpId)
      resetForm()
      toggleCarrier(createdCarrier, false)
      dispatch(carrierActions.createCarrier(createdCarrier))
    } catch (error) {
      let errors: Errors | null = null
      if (error?.inner && Array.isArray(error.inner)) {
        errors = parseYupError(error)
      } else if (error?.response?.status === 400) {
        errors = parseAxiosError(error)
      }
      if (errors) setErrors(errors)
    } finally {
      setCreating(false)
    }
  }, [carrier, dispatch, resetForm, toggleCarrier, rfpId])

  useEffect(() => {
    if (isEditing) inputRef.current?.focus()
  }, [isEditing])

  const makeFieldOnChangeHandler = useCallback(
    (
      fieldName: keyof typeof carrier,
      onChange: (event: React.ChangeEvent<HTMLInputElement>) => void
    ) => {
      return async (event: React.ChangeEvent<HTMLInputElement>) => {
        const fieldErrors = { ...errors, [fieldName]: '' }

        try {
          onChange(event)

          await schema.validateAt(
            fieldName,
            {
              ...carrier,
              [fieldName]: event.target.value,
            },
            { abortEarly: false }
          )
        } catch (error) {
          const yupErrors = parseYupError(error)

          fieldErrors[fieldName] = yupErrors[fieldName]
        }

        setErrors(fieldErrors)
      }
    },
    [carrier, errors]
  )

  const onChangeName = useMemo(
    () => makeFieldOnChangeHandler('name', name.onChange),
    [name.onChange, makeFieldOnChangeHandler]
  )
  const onChangeDot = useMemo(
    () => makeFieldOnChangeHandler('dot', dot.onChange),
    [dot.onChange, makeFieldOnChangeHandler]
  )
  const onChangeEmail = useMemo(
    () => makeFieldOnChangeHandler('email', email.onChange),
    [email.onChange, makeFieldOnChangeHandler]
  )

  return (
    <Wrapper>
      <AddNewButton type="button" variant="secondary" onClick={toggleEditing}>
        ADD NEW CARRIER
      </AddNewButton>
      {isEditing && (
        <FormContainer>
          <Label>
            CARRIER NAME
            <Input
              ref={inputRef}
              disabled={isCreating}
              {...name}
              onChange={onChangeName}
            />
            <Error error={errors.name} />
          </Label>
          <Label>
            DOT NUMBER
            <Input disabled={isCreating} {...dot} onChange={onChangeDot} />
            <Error error={errors.dot} />
          </Label>
          <Label>
            EMAIL
            <Input disabled={isCreating} {...email} onChange={onChangeEmail} />
            <Error error={errors.email} />
          </Label>
          <Label>
            {/* HACK: aligns button with other elements */}
            <InvisibleText>invite</InvisibleText>{' '}
            <InviteButton
              variant="secondary"
              type="button"
              onClick={createAndInvite}
              disabled={isCreating}
            >
              {isCreating ? (
                <Loader width={20} height={20} type="TailSpin" />
              ) : (
                'INVITE'
              )}
            </InviteButton>
          </Label>
        </FormContainer>
      )}
    </Wrapper>
  )
}
